import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import legalDocumentsService from '../api/legalDocumentsService';
import type { CreateLegalDocumentDto, LegalDocument, UpdateLegalDocumentDto } from '../types/types';

export const useLegalDocuments = () => {
    return useQuery<LegalDocument[]>({
        queryKey: ['legalDocuments'],
        queryFn: () => legalDocumentsService.getAll(),
        refetchOnWindowFocus: false,
    });
};

export const useCreateLegalDocument = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: CreateLegalDocumentDto) => legalDocumentsService.create(dto),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['legalDocuments'] }),
    });
};

export const useUpdateLegalDocument = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, dto }: { id: number; dto: UpdateLegalDocumentDto }) =>
            legalDocumentsService.update(id, dto),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['legalDocuments'] }),
    });
};

export const useDeleteLegalDocument = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => legalDocumentsService.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['legalDocuments'] }),
    });
};

export const usePublishLegalDocument = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => legalDocumentsService.publish(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['legalDocuments'] }),
    });
};

export const useSupersedeLegalDocument = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => legalDocumentsService.supersede(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['legalDocuments'] }),
    });
};

export const useSeedLegalDocuments = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => legalDocumentsService.seed(),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['legalDocuments'] }),
    });
};

export const useUploadLegalDocumentContent = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, locale, file }: { id: number; locale: string; file: File }) =>
            legalDocumentsService.uploadContent(id, locale, file),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['legalDocuments'] }),
    });
};
